import React, { useState } from 'react';
import _ from 'underscore';

import { TeamValidator } from '@shoflo/polymer/validators/TeamValidator/TeamValidator';
import { useFormState } from 'react-use-form-state';
import Tooltip from 'components/Tooltip';

import { Row, Col, Input, Form, FormGroup, FormFeedback } from 'reactstrap';

import { Button } from '@shoflo/polymer/components/Button/Button';
import PhotoInput from 'components/PhotoInput';
import styled from 'styled-components';
import StyledFormLabel from 'components/form/StyledFormLabel';
import { getDateFormat } from 'utils/teamHelper';
import HideInPercy from 'components/HideInPercy';
import DateFormatInput from 'components/DateFormatInput';

const ImageSizeLabel = styled.label`
  display: block;
  width: 100%;
  color: #767676;
  font-size: 12px;
`;

const InputHiddenInPercy = HideInPercy(Input);

const EditForm = ({ team, onSubmit, onCancel }) => {
  const [photoFile, setPhotoFile] = useState();
  const [dateFormat, setDateFormat] = useState(getDateFormat(team));

  const [formState, { label, text }] = useFormState({
    title: team.title,
    phone: team.phone,
    email: team.email,
  });

  const { validity, touched } = formState;

  return (
    <div>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          const changes = {};
          const { title, phone, email } = formState.values;
          if (team.title !== title) changes.title = title;
          if (team.phone !== phone) changes.phone = phone;
          if (team.email !== email) changes.email = email;
          onSubmit({ ...changes, dateFormat }, photoFile);
        }}
      >
        <FormGroup>
          <StyledFormLabel id="titleLabel" {...label('title')}>
            Name
          </StyledFormLabel>
          <Tooltip
            id="team-edit-form-name"
            title="The name of your organization"
          />
          <InputHiddenInPercy
            aria-describedby="tip-team-edit-form-name"
            aria-labelledby="titleLabel"
            data-testid="teamSettingsTitleInput"
            {...text({
              name: 'title',
              validate: TeamValidator.title,
              validateOnBlur: false,
              touchOnChange: true,
            })}
            invalid={touched.title && !validity.title}
            required
            className="fs-exclude"
          />

          {validity.title === false && (
            <FormFeedback>
              Uh oh! Team names must be at least 2 characters.
            </FormFeedback>
          )}
        </FormGroup>
        <Row form>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel {...label('phone')}>
                Phone number
              </StyledFormLabel>
              <Tooltip
                id="team-edit-form-phone"
                title="The phone number for the main point of contact at your organization"
              />
              <Input
                {...text({
                  name: 'phone',
                  validate: TeamValidator.phone,
                  validateOnBlur: false,
                  touchOnChange: true,
                })}
                invalid={touched.phone && !validity.phone}
                required
                className="fs-exclude"
                aria-describedby="tip-team-edit-form-phone"
              />
              {validity.phone === false && (
                <FormFeedback>
                  Uh oh! Phone numbers must be at least 4 characters.
                </FormFeedback>
              )}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <StyledFormLabel {...label('email')}>Email</StyledFormLabel>
              <Tooltip
                id="team-edit-form-email"
                title="The email for the main point of contact at your organization"
              />
              <InputHiddenInPercy
                {...text({
                  name: 'email',
                  validate: TeamValidator.email,
                  validateOnBlur: false,
                  touchOnChange: true,
                })}
                invalid={touched.email && !validity.email}
                required
                className="fs-exclude"
                aria-describedby="tip-team-edit-form-email"
              />
              {validity.email === false && (
                <FormFeedback>Uh oh! Must be a valid email</FormFeedback>
              )}
            </FormGroup>
          </Col>
        </Row>
        <Row form>
          <Col md={6}>
            <PhotoInput
              label="Logo"
              data-testid="teamSettingsPhotoUploadInput"
              imageUri={team.photo}
              onChange={setPhotoFile}
              type="team"
              tooltipProps={{
                id: 'team-edit-form-logo',
                placement: 'bottom-start',
                title:
                  'The image used to identify your organization. This logo will be displayed throughout Shoflo',
              }}
            />
            <ImageSizeLabel>JPG, GIF, or PNG. Max size of 2mb.</ImageSizeLabel>
          </Col>

          <Col md={6}>
            <DateFormatInput value={dateFormat} onChange={setDateFormat} />
          </Col>
        </Row>
        <div className="float-end modal-footer">
          <Button
            disabled={!formIsValid(formState)}
            color="primary"
            data-testid="teamSettingsSubmitButton"
          >
            Save
          </Button>{' '}
          <Button
            color="secondary"
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

const formIsValid = (formState) => {
  /**
   * 1. Make sure all fields are valid
   * 2. Make sure all fields were "touched"
   *
   */
  return _.every(formState.validity, (v) => v);
};

export default EditForm;
